import { useState, useRef, useEffect } from 'react';
import { Group } from '@visx/group';
import { Pie } from '@visx/shape';
import { TooltipWithBounds, useTooltip } from '@visx/tooltip';
import { scaleOrdinal } from '@visx/scale';
import { localPoint } from '@visx/event';
import {
	Box,
	Text,
	HStack,
	Grid,
	useBreakpointValue,
	Spinner,
	Alert,
	AlertIcon,
	AlertTitle,
	AlertDescription,
	Flex,
	IconButton,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import usePieChartData from '../../hooks/usePieChartData'; // Предположим, этот хук есть
import { formatNumber } from '../../utils/helper';
import { RiDonutChartFill } from 'react-icons/ri';
import { LuBarChartHorizontal } from 'react-icons/lu';
import BarRaceChart from './BarRaceChart';

const PieChart = () => {
	const { data, loading, error } = usePieChartData();
	const [tooltipData, setTooltipData] = useState(null);
	const { showTooltip, hideTooltip, tooltipLeft, tooltipTop } = useTooltip();
	const [showPieChart, setShowPieChart] = useState(true);
	const svgRef = useRef(null);
	const isMobile = useBreakpointValue({ base: true, md: false });

	const width = useBreakpointValue({ base: 300, md: 450 });
	const height = useBreakpointValue({ base: 300, md: 450 });

	const sortByIndexDescending = (a, b) => b.index - a.index;

	const colors = scaleOrdinal({
		domain: data.sort(sortByIndexDescending).map(d => d.label),
		range: [
			'#f3a683', '#f7d794', '#dbe5d8', '#b9e3c6', '#b0e57c',
			'#ff9f9f', '#c6a0f0', '#76e3f0', '#f0b9e0', '#9de0a4',
		],
	});

	useEffect(() => {
		if (tooltipData && svgRef.current) {
			const rect = svgRef.current.getBoundingClientRect();
			const tooltipOffsetX = rect.left;
			const tooltipOffsetY = rect.top;
			showTooltip({
				tooltipLeft: tooltipLeft - tooltipOffsetX,
				tooltipTop: tooltipTop - tooltipOffsetY,
			});
		}
	}, [tooltipData, tooltipLeft, tooltipTop, showTooltip]);

	const radius = Math.min(width, height) / 2;
	const centerX = width / 2;
	const centerY = height / 2;

	const handleTooltip = (event, data) => {
		const coords = localPoint(event) || { x: 0, y: 0 };
		setTooltipData(data);
		showTooltip({
			tooltipLeft: coords.x,
			tooltipTop: coords.y,
		});
	};

	if (error) {
		return (
			<Box display="flex" alignItems="center" justifyContent="center" height="100vh">
				<Alert status="error">
					<AlertIcon />
					<Box>
						<AlertTitle>Error loading data</AlertTitle>
						<AlertDescription>{error.message || 'An unexpected error occurred'}</AlertDescription>
					</Box>
				</Alert>
			</Box>
		);
	}

	return (
		<Flex flexFlow={isMobile ? "column-reverse" : "row"} gap={4} ml={isMobile ? 0 : 16}>
			<Box
				display="flex"
				flexDirection="column"
				backgroundColor="#FEF0DD"
				borderRadius="1.6rem"
				padding={4}
				width="full"
				height="full"
				alignItems="center"
				justifyContent={"center"}
				overflow="auto"
				position="relative"
			>
				{/* Chart Switcher Buttons */}


				{/* Chart Display */}
				<Flex
					flexDirection={isMobile ? "column" : "row"}
					width="full"
					height="full"
					alignItems="center"
					justifyContent="center"
				// mt={isMobile ? "3rem" : "0"}
				>
					{showPieChart ? (
						<Box
							width={width}
							height={height}
							display="flex"
							justifyContent="center"
							alignItems="center"
							position="relative"
							flex="1"
						>
							{loading ? (
								<Box width={width} height={height} display="flex" alignItems="center" justifyContent="center">
									<Spinner size="xl" color='#FF684B' thickness="4px" />
								</Box>
							) : (
								<svg ref={svgRef} width={width} height={height} style={{ border: 'none' }}>
									<Group top={centerY} left={centerX}>
										<Pie
											data={data}
											pieValue={d => d.value}
											outerRadius={radius - 10}
											innerRadius={radius - 70}
											cornerRadius={3}
											padAngle={0.02}
											pieSort={sortByIndexDescending}
										>
											{pie => {
												return pie.arcs.map(arc => (
													<motion.g
														key={`arc-${arc.data.label}`}
														onMouseEnter={(event) => handleTooltip(event, arc.data)}
														onMouseLeave={() => {
															hideTooltip();
															setTooltipData(null);
														}}
														initial={{ opacity: 0, scale: 0.9 }}
														animate={{ opacity: 1, scale: 1 }}
														transition={{ duration: 0.5 }}
													>
														<path d={pie.path(arc)} fill={colors(arc.data.label)} />
													</motion.g>
												));
											}}
										</Pie>
									</Group>
								</svg>
							)}

							{tooltipData && (
								<TooltipWithBounds
									left={tooltipLeft}
									top={tooltipTop}
									style={{
										position: 'absolute',
										backgroundColor: 'rgba(0,0,0,0.7)',
										color: '#fff',
										padding: '10px',
										borderRadius: '50%',
										fontSize: '0.8rem',
										minWidth: "150px",
										minHeight: "150px",
										textAlign: 'center',
										transform: !isMobile ? 'translate(100%, 100%)' : 'translate(50%, 50%)',
									}}
								>
									<Box pt="40px">
										<Text fontSize="lg" fontWeight="bold">{tooltipData.label}</Text>
										<Text fontSize="md">{tooltipData.percentage} - {formatNumber(tooltipData.value)}</Text>
									</Box>
								</TooltipWithBounds>
							)}
						</Box>
					) : (
						<Box
							width="full"
							height="full"
							display="flex"
							justifyContent="center"
							alignItems="center"
							position="relative"
							flex="1"
							pr={"10px"}
						>
							<BarRaceChart data={data} colors={colors} width={width} height={height} isMobile={isMobile} />
						</Box>
					)}
					{showPieChart && (
						<Box
							marginTop={isMobile ? 4 : 0}
							marginLeft={isMobile ? 0 : 4}
							width="full"
							flex="1"
							display="flex"
							justifyContent={isMobile ? "center" : "flex-start"}
							alignItems="flex-start"
						>
							<Grid
								templateColumns={isMobile ? 'repeat(2, 1fr)' : 'auto'}
								gap={4}
								width="full"
								justifyContent={isMobile ? "center" : "flex-start"}
							>
								{data.map(d => (
									<HStack key={d.label} spacing={2} align="center" marginBottom={2}>
										<Box width={7} height={7} bg={colors(d.label)} borderRadius="md" />
										<Text fontSize={isMobile ? "sm" : "md"}>{d.label} ({d.percentage})</Text>
									</HStack>
								))}
							</Grid>
						</Box>
					)}
				</Flex>
			</Box>
			<Flex
				top={isMobile ? "1rem" : "1rem"}
				right={isMobile ? "auto" : "1rem"}
				flexDirection={isMobile ? "row" : "column"}
				justifyContent={!isMobile ? "start" : "center"}
				alignItems={isMobile ? "center" : "flex-start"}
				gap={2}
				zIndex={10}
			>
				<IconButton
					w={"25px"}
					aria-label="Show Pie Chart"
					icon={<RiDonutChartFill />}
					onClick={() => setShowPieChart(true)}
					backgroundColor={showPieChart ? "#FEF0DD" : "gray.100"}
					color={showPieChart ? "#FF684B" : "black"} // Icon color
					mr={2}
				/>
				<IconButton
					w={"25px"}
					aria-label="Show Bar Race Chart"
					icon={<LuBarChartHorizontal />}
					onClick={() => setShowPieChart(false)}
					backgroundColor={!showPieChart ? "#FEF0DD" : "gray.100"}
					color={!showPieChart ? "#FF684B" : "black"} // Icon color
				/>
			</Flex>

		</Flex>
	);
};

export default PieChart;
